// components/estimates/EstimateVersionDialogs.js
// Modal per gestione versioni e anteprima documento

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { History, Copy, GitBranch, Download } from 'lucide-react';
import { formatCurrency, formatDate, estimateStatusLabels, estimateStatusColors, getBadgeClass } from '../../lib/utils';

// Versions History Modal
export const VersionsModal = ({
  open,
  onOpenChange,
  versions,
  currentId,
  onNavigateToVersion
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#FF7819]" />
          Storico Versioni
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {versions.map((v) => (
          <div 
            key={v.id}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
              v.id === currentId 
                ? 'border-[#FF7819] bg-[#FFF5EE]' 
                : 'border-[#DCDCDC] hover:border-[#FF7819] hover:bg-[#FAFAFA]'
            }`}
            onClick={() => {
              if (v.id !== currentId) {
                onOpenChange(false);
                onNavigateToVersion(v.id);
              }
            }}
            data-testid={`version-${v.version}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[#3C3C3C]">
                  v{v.version}
                </span>
                <span className="text-sm text-[#737373]">
                  {v.estimate_number}
                </span>
                {v.id === currentId && (
                  <span className="px-2 py-0.5 text-xs bg-[#FF7819] text-white rounded-full">
                    Corrente
                  </span>
                )}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getBadgeClass(estimateStatusColors[v.status])}`}>
                {estimateStatusLabels[v.status]}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-[#737373]">
                {formatDate(v.created_at)}
              </span>
              <span className="text-sm font-medium text-[#FF7819]">
                {formatCurrency(v.total)}
              </span>
            </div>
            {v.version_note && (
              <p className="text-xs text-[#737373] mt-1 italic">
                "{v.version_note}"
              </p>
            )}
          </div>
        ))}
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Chiudi
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Create Version Modal
export const CreateVersionModal = ({
  open,
  onOpenChange,
  versionNote,
  setVersionNote,
  onCreateVersion,
  creatingVersion
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Copy className="w-5 h-5 text-[#FF7819]" />
          Crea Nuova Versione
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4">
        <p className="text-sm text-[#737373]">
          Verrà creata una copia di questo preventivo con una nuova versione. 
          Potrai modificarla indipendentemente dall'originale.
        </p>
        
        <div>
          <Label>Nota sulla versione (opzionale)</Label>
          <Textarea
            value={versionNote}
            onChange={(e) => setVersionNote(e.target.value)}
            placeholder="Es: Modifica richiesta dal cliente, Aggiunta opzioni cucina..."
            rows={3}
            data-testid="version-note-textarea"
          />
        </div>
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          className="btn-primary"
          onClick={onCreateVersion}
          disabled={creatingVersion}
          data-testid="confirm-create-version-btn"
        >
          {creatingVersion ? (
            <>Creazione...</>
          ) : (
            <>
              <GitBranch className="w-4 h-4 mr-2" />
              Crea Versione
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Document Preview Modal
export const PreviewModal = ({
  open,
  onOpenChange,
  previewMode,
  previewHtml,
  loadingPreview,
  onPreview,
  onDownloadPdf
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
      <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
        <DialogTitle className="flex items-center justify-between pr-8">
          <span>Anteprima {previewMode === 'customer' ? 'Cliente' : 'Completo'}</span>
          <div className="flex items-center gap-2">
            <Button
              variant={previewMode === 'customer' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPreview('customer')}
              className={previewMode === 'customer' ? 'bg-[#FF7819] hover:bg-[#E56A10]' : ''}
            >
              Cliente
            </Button>
            <Button
              variant={previewMode === 'detailed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPreview('detailed')}
              className={previewMode === 'detailed' ? 'bg-[#FF7819] hover:bg-[#E56A10]' : ''}
            >
              Completo
            </Button>
          </div>
        </DialogTitle>
      </DialogHeader>
      <div className="flex-1 overflow-auto bg-gray-100 p-4">
        {loadingPreview ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF7819]"></div>
          </div>
        ) : (
          <div className="bg-white shadow-lg mx-auto" style={{ maxWidth: '210mm' }}>
            <iframe
              srcDoc={previewHtml}
              title="Document Preview"
              className="w-full"
              style={{ height: '1200px', border: 'none' }}
            />
          </div>
        )}
      </div>
      <DialogFooter className="px-6 py-4 border-t shrink-0 bg-gray-50">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Chiudi
        </Button>
        <Button 
          className="bg-[#FF7819] hover:bg-[#E56A10] text-white"
          onClick={() => {
            onDownloadPdf(previewMode === 'customer' ? 'client' : 'full');
            onOpenChange(false);
          }}
          data-testid="download-from-preview-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Scarica PDF
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
